import { useTranslation } from "react-i18next";
import { Box, TableCell, TableRow, TableBody, Table, TableContainer, Typography } from "@mui/material";

import { GoBasicPlanIcon } from "../../../assets/icons/plans-icons";
import { WebSubscriptionTypePlan } from "../../../types/plans";
import { useCheckUserSubscription } from "../../../api/queries/useGetUserSettings";
import { useGetWebSubscriptionPlans } from "../../../api/queries/useGetCurrentWebSubscriptionPlan";

import FeaturesCell from "./FeaturesCell";
import { plansListStyles } from "./styles";
import { PlanDescriptionCell, PlanDescriptionCellLoader, PlanActionCell, PlanActionCellLoader } from "./PlanCells";

const PlansList = () => {
  const { t } = useTranslation("plans", { keyPrefix: "page.plansPage" });
  const { data: webSubscriptionPlans, isLoading: isWebSubscriptionPlansLoading } = useGetWebSubscriptionPlans();
  const { data: userSubscription, isLoading: isUserSubscriptionLoading } = useCheckUserSubscription();

  const combinedPlans =
    webSubscriptionPlans?.map((plan) => ({
      ...plan,
      plans: plan.subscriptionPlans,
    })) ?? [];

  const formatPlanFeatures = (plan: WebSubscriptionTypePlan) => {
    const features: { title: string; features: string[] }[] = [];

    if (plan.webAndApp && plan.webAndApp.length > 0) {
      features.push({
        title: t("webAndApp"),
        features: plan.webAndApp,
      });
    }

    if (plan.webOnly && plan.webOnly.length > 0) {
      features.push({
        title: t("webOnly"),
        features: plan.webOnly,
      });
    }

    if (plan.appOnly && plan.appOnly.length > 0) {
      features.push({
        title: t("appOnly"),
        features: plan.appOnly,
      });
    }

    return features;
  };

  // const formatGoBasicFeatures = () => {
  //   const features: { title: string; features: string[] }[] = [];

  //   // Web & App features
  //   features.push({
  //     title: t("webAndApp"),
  //     features: [
  //       t("goBasicFeatures.webAndApp.unlimitedPicVideoPosting"),
  //       t("goBasicFeatures.webAndApp.createUnlimitedEvents"),
  //       t("goBasicFeatures.webAndApp.joinAllEvents"),
  //       t("goBasicFeatures.webAndApp.sendInvitesUpTo44InYourListForEvents"),
  //       t("goBasicFeatures.webAndApp.appearInSearchEngines"),
  //       t("goBasicFeatures.webAndApp.notPartOfTheFeaturedGoalngoListUnlessYouAreVerified"),
  //       t("goBasicFeatures.webAndApp.receiveUnlimitedTestimonials"),
  //       t("goBasicFeatures.webAndApp.uploadUpTo30SecondsVideos"),
  //       t("goBasicFeatures.webAndApp.unlimitedConnectionWithTheSamePersona"),
  //       t("goBasicFeatures.webAndApp.upTo5ConnectionsWithOtherPersonaPerDay"),
  //       t("goBasicFeatures.webAndApp.basicAnalyticsOnProfilePerformance"),
  //     ],
  //   });

  //   // Web-Only features
  //   features.push({
  //     title: t("webOnly"),
  //     features: [t("goBasicFeatures.webOnly.paymentLinks"), t("goBasicFeatures.webOnly.hireJourney")],
  //   });

  //   // App-Only features
  //   features.push({
  //     title: t("appOnly"),
  //     features: [
  //       t("goBasicFeatures.appOnly.getVerifiedForFree"),
  //       t("goBasicFeatures.appOnly.createUnlimitedChallenges"),
  //       t("goBasicFeatures.appOnly.join5ChallengesPerMonth"),
  //       t("goBasicFeatures.appOnly.sendInvitesUpTo4InYourListForChallenges"),
  //       t("goBasicFeatures.appOnly.giveUnlimitedTestimonials"),
  //       t("goBasicFeatures.appOnly.fullAccessToWhoViewedYourProfile"),
  //       t("goBasicFeatures.appOnly.uploadUpTo30SecondsVideos"),
  //       t("goBasicFeatures.appOnly.sendGoReachMessagesPerPricing"),
  //     ],
  //   });

  //   return features;
  // };

  // const goBasicFeatures = formatGoBasicFeatures();
  const totalColumns = isWebSubscriptionPlansLoading ? 3 : 1 + combinedPlans.length; // GoBasic + subscription plans
  const columnWidth = `${100 / totalColumns}%`;

  return (
    <TableContainer sx={plansListStyles.tableContainer}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        {/* <colgroup>
          <col style={{ width: columnWidth }} />
          {!isWebSubscriptionPlansLoading &&
            combinedPlans.map((_, index) => <col key={`col-${index}`} style={{ width: columnWidth }} />)}
          {isWebSubscriptionPlansLoading &&
            [...Array(3)].map((_, index) => <col key={`loader-col-${index}`} style={{ width: columnWidth }} />)}
        </colgroup> */}
        <TableBody>
          <TableRow>
            {/* <GoBasicPlanDescriptionCell /> */}
            {!isWebSubscriptionPlansLoading &&
              combinedPlans.map((combinedPlan) => (
                <PlanDescriptionCell key={combinedPlan.id} combinedPlan={combinedPlan} />
              ))}
            {isWebSubscriptionPlansLoading &&
              [...Array(3)].map((_, index) => <PlanDescriptionCellLoader key={index} />)}
          </TableRow>
          <TableRow>
            {/* <FeaturesCell features={goBasicFeatures} /> */}
            {!isWebSubscriptionPlansLoading &&
              combinedPlans.map((combinedPlan) => (
                <FeaturesCell key={combinedPlan.id} features={formatPlanFeatures(combinedPlan)} />
              ))}
            {isWebSubscriptionPlansLoading &&
              [...Array(3)].map((_, index) => <FeaturesCell key={index} features={[]} />)}
          </TableRow>
          <TableRow
            sx={{
              "&:nth-of-type(odd)": { backgroundColor: "var(--Gray-50, #F9FAFB)" },
            }}
          >
            {/* <TableCell component="td" scope="row" sx={plansListStyles.tablePlanActionItemCell}></TableCell> */}
            {!isWebSubscriptionPlansLoading &&
              combinedPlans.map((combinedPlan) => (
                <PlanActionCell
                  key={combinedPlan.id}
                  combinedPlan={combinedPlan}
                  userSubscription={userSubscription}
                  isLoading={isUserSubscriptionLoading}
                />
              ))}
            {isWebSubscriptionPlansLoading && [...Array(3)].map((_, index) => <PlanActionCellLoader key={index} />)}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PlansList;

// const GoBasicPlanDescriptionCell = () => {
//   const { t } = useTranslation("plans", { keyPrefix: "page.plansPage" });

//   return (
//     <TableCell component="td" scope="row" sx={plansListStyles.tablePlanItemCell}>
//       <Box sx={plansListStyles.planContainer}>
//         <GoBasicPlanIcon sx={{ width: 40, height: 40 }} />
//         <Box sx={plansListStyles.planTitleContainer}>
//           <Typography sx={plansListStyles.planTitle}>{t("goBasic")}</Typography>
//           <Typography
//             title={t("allTheEssentialsStartFreeAndExploreWithConfidence")}
//             sx={plansListStyles.planDescription}
//           >
//             {t("allTheEssentialsStartFreeAndExploreWithConfidence")}
//           </Typography>
//         </Box>
//         <Box sx={plansListStyles.planPriceContainer}>
//           <Typography sx={plansListStyles.planPrice}>{t("free")}</Typography>
//         </Box>
//         <Box sx={plansListStyles.creditsContainer}>
//           <Typography sx={plansListStyles.creditsTitle}>{t("numberOfCredits")}</Typography>
//           <Typography sx={plansListStyles.creditsValue}>{500}</Typography>
//         </Box>
//       </Box>
//     </TableCell>
//   );
// };
